
/**
 * TODO:
 * 現状は各情報を都度fetchしているが、「現在のポイント」情報などをストアにキャッシュする機構を実装する
 */

import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useDialogWithRouter } from '@/compositions/useDialogWithRouter';
import Coupon from '@/components/organisms/coupon/Coupon.vue';
import PointInfo from '@/components/organisms/point/PointInfo.vue';
import { useCoupon } from '@/compositions/useCoupon';
import { usePoint } from '@/compositions/usePoint';
import { Coupon as CouponType } from '@/repositories/couponRepository';
import axios from 'axios';
import { useRouter } from 'vue-router/composables';
import { ToastProgrammatic as Toast } from 'buefy';

type State = {
  isLoading: boolean;
  step: 'CONFIRM' | 'COMPLETE' | 'FAILED';
  initialized: boolean;
  statusCode?: number;
  restricted: boolean;
};

type Props = {
  coupon: CouponType;
};

export default defineComponent<Props>({
  name: 'DialogCouponExchangeConfirm',

  components: {
    Coupon,
    PointInfo,
  },

  props: {
    coupon: {
      type: Object,
      required: true,
    },
  },

  setup(props: Props, { emit }) {
    const router = useRouter();
    const { dialog, open, close, afterClose } = useDialogWithRouter(router);
    const { exchange, canExchange } = useCoupon();
    const { enabled: pointEnabled, get: getPoint } = usePoint();

    const state = reactive<State>({
      isLoading: false,
      step: 'CONFIRM',
      initialized: false,
      statusCode: undefined,
      restricted: false,
    });

    /**
     * 現在のポイント
     */
    const currentPoint = ref<number | null>(null);

    /**
     * クーポン獲得に必要なポイント
     */
    const couponPoint = computed(() => props.coupon.couponMaster.point);

    /**
     * 交換後の残りポイント
     */
    const remainingPoint = computed(() => {
      if (currentPoint.value === null) return null;
      return currentPoint.value - props.coupon.couponMaster.point;
    });

    /**
     * 交換に必要な不足ポイント
     */
    const shortagePoint = computed(() => {
      if (remainingPoint.value === null) return null;
      return remainingPoint.value * -1;
    });

    /**
     * 交換可能かどうか
     * @note クーポン交換後に0ポイント以上残るなら交換可能
     */
    const exchangable = computed(() => {
      if (remainingPoint.value === null) return false;
      return remainingPoint.value >= 0;
    });

    /**
     * 交換制御されているかどうか
     */
    const restrictedCoupon = async () => {
      const result = await canExchange(props.coupon);
      state.restricted = !result.data.exchangable;
    };

    /**
     * ポイントをクーポンに交換する
     */
    const exchangeCoupon = async () => {
      state.isLoading = true;
      try {
        // クーポン交換
        await exchange(props.coupon.id);
        // ポイント情報再取得
        const { total } = await getPoint();
        currentPoint.value = total;

        state.step = 'COMPLETE';
        Toast.open({
          message: couponPoint.value > 0 ? '交換しました' : '取得しました',
          type: 'is-success',
          position: 'is-top',
        });
        emit('exchange'); // 交換完了イベントを送出して、クーポン一覧を再読み込みする
      } catch (e) {
        state.step = 'FAILED';
        if (!axios.isAxiosError(e)) throw e;

        if (e.response) {
          state.statusCode = e.response.status;
          if (e.response.status === 419) {
            // 在庫なし
            emit('empty-stock-coupon', props.coupon.id);
          }
          if (e.response.status === 420) {
            // 交換制限
            emit('restricted', props.coupon.id);
          }
          // 想定外のエラーの場合はthrowする
          if (![419, 420].includes(e.response.status)) throw e;
        }
      } finally {
        state.isLoading = false;
      }
    };

    onMounted(async () => {
      open();
      // ポイント情報再取得
      const { total } = await getPoint();
      currentPoint.value = total;
      state.initialized = true;
      restrictedCoupon();
    });

    return {
      // dialog
      dialog,
      close,
      afterClose: () => {
        afterClose();
        emit('close');
      },

      state,
      pointEnabled,
      currentPoint,
      couponPoint,
      remainingPoint,
      shortagePoint,
      exchangable,
      exchangeCoupon,
      restrictedCoupon,
    };
  },
});
